// ===========================================
// Drive Folder Picker Component
// ===========================================
// Reusable component for selecting/linking Google Drive folders

import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Divider,
  Skeleton,
  Chip,
  Link,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ArrowBack as BackIcon,
  Home as HomeIcon,
  OpenInNew as OpenInNewIcon,
  LinkOff as UnlinkIcon,
} from '@mui/icons-material';
import {
  useBrowseFolders,
  useLinkFolder,
  useUnlinkFolder,
  useFolderMappings,
  useGoogleDriveAuthStatus,
} from '../../hooks/useGoogleDrive';
import { usePermissions } from '../../hooks/usePermissions';
import { DriveFolder } from '../../api/googleDrive.api';

// ===========================================
// TYPES
// ===========================================

interface DriveFolderPickerProps {
  lessonId?: string;
  studentId?: string;
  programLevelId?: string;
  onLinked?: () => void;
  onUnlinked?: () => void;
}

// ===========================================
// COMPONENT
// ===========================================

export default function DriveFolderPicker({
  lessonId,
  studentId,
  programLevelId,
  onLinked,
  onUnlinked,
}: DriveFolderPickerProps) {
  const { can } = usePermissions();
  // Link, change and unlink need integrations.write; anyone with read still sees the linked folder (roles plan 5.6).
  const canManage = can('integrations', 'write');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [browsePath, setBrowsePath] = useState<{ id: string | null; name: string }[]>([
    { id: null, name: 'My Drive' },
  ]);
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);

  // Current folder for browsing
  const currentFolderId = browsePath.length > 1 ? browsePath[browsePath.length - 1].id : undefined;

  // Queries
  const { data: authStatus } = useGoogleDriveAuthStatus();
  const { data: folders, isLoading: isLoadingFolders } = useBrowseFolders(
    { parentId: currentFolderId ?? undefined },
    { enabled: dialogOpen && authStatus?.isConnected }
  );
  const { data: allMappings } = useFolderMappings();

  // Mutations
  const linkMutation = useLinkFolder();
  const unlinkMutation = useUnlinkFolder();

  const isConnected = authStatus?.isConnected ?? false;

  // Find existing mapping for this lesson/student/programLevel
  const existingMapping = allMappings?.find(
    (m) =>
      // Scalar FKs — `m.lesson?.id` etc. never arrived, so a linked folder looked
      // unlinked and re-linking 409'd (#202).
      (lessonId && m.lessonId === lessonId) ||
      (studentId && m.studentId === studentId) ||
      (programLevelId && m.programLevelId === programLevelId)
  );

  // Handle folder navigation
  const handleFolderClick = (folder: DriveFolder) => {
    setBrowsePath([...browsePath, { id: folder.id, name: folder.name }]);
  };

  const handleBackClick = () => {
    if (browsePath.length > 1) {
      setBrowsePath(browsePath.slice(0, -1));
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    setBrowsePath(browsePath.slice(0, index + 1));
  };

  // Handle selecting a folder to link
  const handleSelectFolder = async (folder: DriveFolder) => {
    try {
      // If there's an existing mapping, unlink it first
      if (existingMapping) {
        await unlinkMutation.mutateAsync(existingMapping.id);
      }

      await linkMutation.mutateAsync({
        driveFolderId: folder.id,
        folderName: folder.name,
        folderUrl: folder.webViewLink,
        lessonId,
        studentId,
        programLevelId,
      });
      setDialogOpen(false);
      setBrowsePath([{ id: null, name: 'My Drive' }]);
      onLinked?.();
    } catch {
      // Error is handled by mutation onError (shows snackbar)
    }
  };

  // Handle unlinking
  const handleUnlink = async () => {
    if (!existingMapping) return;
    try {
      await unlinkMutation.mutateAsync(existingMapping.id);
      setUnlinkDialogOpen(false);
      onUnlinked?.();
    } catch {
      // Error is handled by mutation onError (shows snackbar)
    }
  };

  // Reset browse path when dialog opens
  const handleOpenDialog = () => {
    setBrowsePath([{ id: null, name: 'My Drive' }]);
    setDialogOpen(true);
  };

  // Don't render if not connected
  if (!isConnected) {
    return null;
  }

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: existingMapping ? 'primary.light' : 'grey.100',
            }}
          >
            {existingMapping ? (
              <FolderOpenIcon sx={{ color: 'primary.main' }} />
            ) : (
              <FolderIcon sx={{ color: 'grey.400' }} />
            )}
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Google Drive Folder
            </Typography>
            {existingMapping ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" fontWeight="medium">
                  {existingMapping.folderName}
                </Typography>
                <Chip
                  label={`${existingMapping._count?.GoogleDriveFile || 0} files`}
                  size="small"
                  variant="outlined"
                />
                <Link
                  href={existingMapping.folderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <OpenInNewIcon fontSize="small" />
                </Link>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No folder linked
              </Typography>
            )}
          </Box>

          {canManage && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {existingMapping && (
              <Tooltip title="Unlink folder">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setUnlinkDialogOpen(true)}
                  disabled={unlinkMutation.isPending}
                >
                  <UnlinkIcon />
                </IconButton>
              </Tooltip>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenDialog}
              disabled={linkMutation.isPending}
            >
              {existingMapping ? 'Change' : 'Link Folder'}
            </Button>
          </Box>
          )}
        </Box>
      </Paper>

      {/* Folder Picker Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Google Drive Folder</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a folder to link. Files in this folder will sync with the portal.
          </Typography>

          {/* Breadcrumb navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {browsePath.length > 1 && (
              <Button
                size="small"
                startIcon={<BackIcon />}
                onClick={handleBackClick}
              >
                Back
              </Button>
            )}
            <Breadcrumbs>
              {browsePath.map((item, index) => (
                <Button
                  key={index}
                  size="small"
                  onClick={() => handleBreadcrumbClick(index)}
                  disabled={index === browsePath.length - 1}
                  startIcon={index === 0 ? <HomeIcon fontSize="small" /> : undefined}
                  sx={{ textTransform: 'none', minWidth: 'auto' }}
                >
                  {item.name}
                </Button>
              ))}
            </Breadcrumbs>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Folder list */}
          {isLoadingFolders ? (
            <Box sx={{ py: 2 }}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : folders && folders.length > 0 ? (
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {folders.map((folder) => (
                <ListItemButton
                  key={folder.id}
                  onClick={() => handleFolderClick(folder)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemIcon>
                    <FolderIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={folder.name} />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectFolder(folder);
                    }}
                    disabled={linkMutation.isPending}
                  >
                    {linkMutation.isPending ? (
                      <CircularProgress size={16} />
                    ) : (
                      'Select'
                    )}
                  </Button>
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No subfolders in this location
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Unlink Confirmation Dialog */}
      <Dialog open={unlinkDialogOpen} onClose={() => setUnlinkDialogOpen(false)}>
        <DialogTitle>Unlink Folder</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to unlink "{existingMapping?.folderName}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Files will remain in the portal but will no longer sync with Google Drive.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlinkDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleUnlink}
            disabled={unlinkMutation.isPending}
          >
            {unlinkMutation.isPending ? 'Unlinking...' : 'Unlink'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
